import React from 'react'
import clsx from 'clsx'
import makeStyles from '@mui/styles/makeStyles'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import MediaAvatar from '../MediaAvatar'

import { useAgent } from '@SonarCX/providers/AgentProvider'
import Checkbox from '@SonarCX/pages/Settings/Controls/Checkbox'
import {
  addSelected,
  removeSelected,
  selectContactsTable
} from '@SonarCX/slices/contactsTable'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },

  avatar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    fontSize: theme.size.sm
  },

  name: {
    marginLeft: '5px',
    fontSize: theme.size.sm,
    color: '#323436'
  },

  checkbox: {
    zIndex: 10,
    position: 'absolute',
    left: '1rem',
    borderRadius: 4,
    transform: 'translateX(-50%)',
    backgroundColor: 'white',

    '& > span': {
      marginRight: 0
    }
  }
}))

const NamedAvatar = ({
  user,
  markMe,
  hideName,
  avatarClassName,
  className,
  intersect,
  customName,
  selectable = false
}) => {
  const dispatch = useAppDispatch()
  const { selected, showSelector } = useAppSelector(selectContactsTable)
  const { currentUser } = useAgent()
  const [showCheckbox, setShowCheckbox] = React.useState(false)

  const classes = useStyles()
  const isSelected = React.useMemo(
    () => selected.some(x => x === user?.id),
    [selected, user?.id]
  )

  const shouldRenderCheckbox = React.useMemo(() => {
    if (!selectable) {
      return false
    } else if (showSelector) {
      return true
    } else if (showCheckbox && selectable) {
      return true
    }
    return false
  }, [showSelector, showCheckbox, selectable])

  const onMouseEnter = React.useCallback(() => {
    setShowCheckbox(true)
  }, [])
  const onMouseLeave = React.useCallback(() => {
    setShowCheckbox(false)
  }, [])

  const handleCheckboxClick = React.useCallback(
    e => {
      if (isSelected) {
        dispatch(removeSelected(user?.id))
      } else {
        dispatch(addSelected(user?.id))
      }
    },
    [user, isSelected, dispatch]
  )

  let nameElem = customName || null
  if (user && !hideName && !nameElem) {
    const isMe = user.id === currentUser()?.id
    nameElem = (
      <div className={clsx(classes.name, className)}>
        {user.name} {markMe && isMe && <>(me)</>}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {shouldRenderCheckbox && (
        <Checkbox
          onCheck={handleCheckboxClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={classes.checkbox}
          checked={isSelected}
        />
      )}

      <MediaAvatar
        user={user}
        className={clsx(classes.avatar, avatarClassName)}
        intersect={intersect}
        onMouseEnter={selectable && onMouseEnter}
        onMouseLeave={selectable && onMouseLeave}
      />

      {nameElem}
    </div>
  )
}

export default NamedAvatar
